import React from 'react';
import { motion } from 'framer-motion';

interface CircularProgressProps {
  value: number;
  maxValue: number;
  size?: number;
  strokeWidth?: number;
  color: string;
  label: string;
  showValue?: boolean;
  valueLabel?: string;
  animated?: boolean;
  targetValue?: number;
  ringIndex?: number;
  totalRings?: number;
  icon?: React.ReactNode;
  centerContent?: React.ReactNode;
}

export function CircularProgress({
  value,
  maxValue,
  size = 120,
  strokeWidth = 8,
  color,
  label,
  showValue = true,
  valueLabel,
  animated = false,
  targetValue,
  ringIndex = 0,
  totalRings = 1,
  icon,
  centerContent
}: CircularProgressProps) {
  // Calculate ring-specific dimensions
  const ringGap = 4; // Gap between rings
  const totalRingWidth = strokeWidth * totalRings + ringGap * (totalRings - 1);
  const outerRadius = (size - strokeWidth) / 2;
  const radius = outerRadius - (ringIndex * (strokeWidth + ringGap));
  
  const circumference = radius * 2 * Math.PI;
  const progress = (value / maxValue) * 100;
  const targetProgress = targetValue ? (targetValue / maxValue) * 100 : 0;
  const offset = circumference - (progress / 100) * circumference;
  const targetOffset = circumference - (targetProgress / 100) * circumference;

  return (
    <div className="absolute inset-0" style={{ width: size, height: size }}>
      <svg 
        width={size} 
        height={size}
        style={{ 
          transform: 'rotate(-90deg)',
          position: 'absolute',
          inset: 0,
          zIndex: totalRings - ringIndex
        }}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-muted/30"
          strokeLinecap="round"
        />
        
        {/* Target progress circle */}
        {targetValue && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={targetOffset}
            className={`stroke-${color}/20`}
            strokeLinecap="round"
          />
        )}
        
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`stroke-${color}`}
          strokeLinecap="round"
        >
          {animated && (
            <animate
              attributeName="stroke-dashoffset"
              from={circumference}
              to={offset}
              dur="1.5s"
              fill="freeze"
              calcMode="spline"
              keySplines="0.4 0 0.2 1"
            />
          )}
        </circle>
      </svg>

      {/* Center content - only show for outermost ring */}
      {ringIndex === 0 && centerContent}
    </div>
  );
}